import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Root = styled.div`
  max-width: 68ch;
  color: var(--text-2);

  > :first-child {
    margin-top: 0;
  }

  a,
  code {
    overflow-wrap: anywhere;
  }

  h2 {
    font-family: var(--font-display);
    font-size: clamp(1.5rem, 2.4vw, 2rem);
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--text-1);
    margin-top: 2.5rem;
  }

  h3 {
    font-size: var(--fs-h3);
    font-weight: 600;
    color: var(--text-1);
    margin-top: 2rem;
  }

  h4 {
    font-family: var(--font-body);
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--text-1);
    margin-top: 2rem;
  }

  p {
    margin-top: 1.25rem;
  }

  strong {
    color: var(--text-1);
    font-weight: 600;
  }

  a {
    color: var(--text-1);
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: color 0.18s ease;

    &:hover {
      color: var(--accent);
    }
  }

  ul,
  ol {
    padding-left: 1.25rem;
    margin-top: 1.25rem;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li {
    margin-top: 0.5rem;
  }

  li::marker {
    color: var(--text-3);
  }

  blockquote {
    border-left: 3px solid var(--accent);
    padding-left: 1.25rem;
    color: var(--text-1);
    margin-top: 1.25rem;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.9em;
    padding: 0.15em 0.4em;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border);
  }

  pre {
    background: var(--bg-1);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    padding: 1.25rem;
    overflow-x: auto;
    font-size: var(--fs-small);
    line-height: 1.6;
    margin-top: 1.25rem;
  }

  pre code {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: none;
    font-size: inherit;
    overflow-wrap: normal;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--radius-md);
    border: 1px solid var(--border);
    margin-top: 1.25rem;
  }

  figure {
    margin-top: 1.25rem;
  }

  figure img {
    margin-top: 0;
  }

  figcaption {
    font-size: var(--fs-small);
    color: var(--text-3);
    margin-top: 0.75rem;
  }

  .table-wrap {
    overflow-x: auto;
    margin-top: 1.25rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-small);
  }

  th {
    color: var(--text-1);
    font-weight: 600;
    background: var(--bg-1);
  }

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border);
    text-align: left;
  }

  hr {
    border: 0;
    border-top: 1px solid var(--border);
    margin: 2.5rem 0;
  }

  input[type='checkbox'] {
    accent-color: var(--accent);
    margin-right: 0.4rem;
  }
`;

interface ProseProps {
  html: string;
}

// The HTML comes from Markdown in this repo, rendered by scripts/build-blog.mjs, which rejects raw HTML.
const Prose: React.FC<ProseProps> = ({ html }) => {
  const navigate = useNavigate();

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement).closest('a');
      const href = anchor?.getAttribute('href');
      if (!anchor || !href || !href.startsWith('/') || href.startsWith('//') || anchor.hasAttribute('target')) return;
      event.preventDefault();
      navigate(href);
    },
    [navigate]
  );

  return <Root onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default Prose;
